import {
  useDeleteProviderKeyMutation,
  useGetAiUserSettingsQuery,
  useSetActiveProviderKeyMutation,
  useSetInactiveProviderKeyMutation,
  useUpsertAiUserSettingsMutation
} from '@/app/Settings/api/aiSettingsApi';
import TextLoading from '@/app/common/components/skeleton/TextLoading';
import ProviderKeysTable from '@/app/Settings/components/ai-settings/ProviderKeysTable';
import type { ProviderKey } from '@/app/Settings/types/aiSettingsApiTypes';
import { errorMessage, successMessage } from '@/utils/toast';
import { faLightbulb } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Accordion, Alert, Card, Col, Form, Row } from 'react-bootstrap';
import { useOutletContext } from 'react-router-dom';
import type { SettingsOutletContext } from '../index';

type AiMode = 'auto' | 'custom';

type ApiErrorShape = {
  data?: { msg?: string };
};

const getApiErrorMessage = (error: unknown, fallback: string): string => {
  const err = error as ApiErrorShape;
  return err?.data?.msg || fallback;
};

const AiSettings = () => {
  const [, setBreadcrumbData] = useOutletContext<SettingsOutletContext>();
  const [aiMode, setAiMode] = useState<AiMode>('auto');
  const [isAiModeInitialized, setIsAiModeInitialized] = useState(false);
  const [providerKeysState, setProviderKeysState] = useState<ProviderKey[]>([]);
  const aiModeUpdateLockRef = useRef(false);

  const {
    data: aiSettingsRes,
    isFetching: isFetchingAiUserSettings,
    isSuccess
  } = useGetAiUserSettingsQuery({});

  useEffect(() => {
    setBreadcrumbData([{ text: 'تنظیمات AI', href: '#' }]);
  }, [setBreadcrumbData]);

  const [deleteProviderKey, { isLoading: isDeletingProviderKey }] =
    useDeleteProviderKeyMutation();
  const [setActiveProviderKey] = useSetActiveProviderKeyMutation();
  const [setInactiveProviderKey] = useSetInactiveProviderKeyMutation();
  const [upsertAiUserSettings] = useUpsertAiUserSettingsMutation();

  const aiSettingsFlags = aiSettingsRes?.settings;

  useEffect(() => {
    if (isAiModeInitialized || !isSuccess) return;
    setAiMode(aiSettingsFlags?.enable_user_ai ? 'custom' : 'auto');
    setIsAiModeInitialized(true);
  }, [aiSettingsFlags, isAiModeInitialized, isSuccess]);

  useEffect(() => {
    setProviderKeysState(aiSettingsRes?.provider_keys ?? []);
  }, [aiSettingsRes]);

  const handleToggleApiKeyStatus = async (keyId: string, isActive: boolean) => {
    try {
      if (isActive) {
        await setActiveProviderKey({ id: keyId }).unwrap();
      } else {
        await setInactiveProviderKey({ id: keyId }).unwrap();
      }
    } catch (err) {
      errorMessage(getApiErrorMessage(err, 'خطا در تغییر وضعیت API Key'));
    }
  };

  const handleDeleteApiKey = async (keyId: string) => {
    try {
      await deleteProviderKey({ id: keyId }).unwrap();
      setProviderKeysState(prev => prev.filter(item => item.id !== keyId));
      successMessage('کلید API با موفقیت حذف شد');
    } catch (err) {
      errorMessage(getApiErrorMessage(err, 'خطا در حذف API Key'));
      throw err;
    }
  };

  const handleAiModeChange = async (nextMode: AiMode) => {
    setAiMode(nextMode);

    if (aiModeUpdateLockRef.current) return;
    aiModeUpdateLockRef.current = true;
    try {
      await upsertAiUserSettings({
        enable_binesh_ai: nextMode === 'auto',
        enable_user_ai: nextMode === 'custom'
      }).unwrap();
      successMessage('تنظیمات AI با موفقیت ذخیره شد');
    } catch (err) {
      errorMessage(getApiErrorMessage(err, 'خطا در ذخیره تنظیمات AI'));
    } finally {
      aiModeUpdateLockRef.current = false;
    }
  };

  const handleModeKeyDown = (
    e: KeyboardEvent<HTMLDivElement>,
    mode: AiMode
  ) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleAiModeChange(mode);
    }
  };

  return (
    <Row className="g-0 justify-content-center">
      <Col>
        <Card>
          <Card.Body className="p-3">
            <div className="text-muted fs--1 mt-1">
              یکی از گزینه‌های زیر را انتخاب کنید.
            </div>

            <div className="mt-3 d-flex flex-column flex-md-row gap-2">
              {isFetchingAiUserSettings ? (
                <>
                  {[1, 2].map(item => (
                    <div
                      key={item}
                      className="border rounded p-3 flex-fill border-300"
                    >
                      <TextLoading width={40} height={18} />
                      <div className="mt-2">
                        <TextLoading width={95} height={14} />
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => handleAiModeChange('auto')}
                    onKeyDown={e => handleModeKeyDown(e, 'auto')}
                    className={`border rounded p-3 flex-fill ${
                      aiMode === 'auto' ? 'border-primary' : 'border-300'
                    }`}
                  >
                    <Form.Check
                      type="radio"
                      id="ai-mode-auto"
                      name="aiMode"
                      checked={aiMode === 'auto'}
                      onChange={() => handleAiModeChange('auto')}
                      label={
                        <div>
                          <div className="fw-semibold">AI خودکار (کیف پول)</div>
                          <div className="text-muted fs--1 mt-1">
                            بدون نیاز به کلید API، فقط با شارژ کیف پول استفاده
                            کنید.
                          </div>
                        </div>
                      }
                    />
                  </div>

                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => handleAiModeChange('custom')}
                    onKeyDown={e => handleModeKeyDown(e, 'custom')}
                    className={`border rounded p-3 flex-fill ${
                      aiMode === 'custom' ? 'border-primary' : 'border-300'
                    }`}
                  >
                    <Form.Check
                      type="radio"
                      id="ai-mode-custom"
                      name="aiMode"
                      checked={aiMode === 'custom'}
                      onChange={() => handleAiModeChange('custom')}
                      label={
                        <div>
                          <div className="fw-semibold">کلید API شخصی</div>
                          <div className="text-muted fs--1 mt-1">
                            از کلید خودتان استفاده کنید و مدیریت هزینه‌ها با
                            شماست.
                          </div>
                        </div>
                      }
                    />
                  </div>
                </>
              )}
            </div>

            {aiMode === 'auto' ? (
              <Alert variant="info" className="mt-3 mb-0 fs--1">
                هزینه استفاده از AI در حالت <b>خودکار</b> از کیف پول شما کسر
                می‌شود. لطفا برای ادامه، کیف پول‌تان را شارژ کنید.
              </Alert>
            ) : (
              <>
                <Alert variant="info" className="mt-3 mb-0 fs--1">
                  در این حالت، درخواست‌ها با <b>کلید API شخصی</b> شما ارسال
                  می‌شود و هزینه‌ها طبق تعرفه ارائه‌دهنده API، از حساب شما نزد
                  همان سرویس‌دهنده کسر خواهد شد.
                </Alert>
                <Accordion className="mt-2 gemini-guide-accordion">
                  <Accordion.Item eventKey="0" className="border-warning">
                    <Accordion.Header>
                      <span className="d-inline-flex align-items-center fs--1 fw-semibold">
                        <FontAwesomeIcon
                          icon={faLightbulb}
                          className="me-2 text-warning"
                          style={{ fontSize: '1.15rem' }}
                        />
                        راهنمای دریافت کلید Gemini
                      </span>
                    </Accordion.Header>
                    <Accordion.Body className="fs--1 bg-warning bg-opacity-10">
                      <ol className="mb-0 pe-3">
                        <li>
                          قبل از ورود به سایت، حتماً <b>VPN</b> را روشن کنید.
                        </li>
                        <li>
                          وارد صفحه ساخت کلید شوید:{' '}
                          <a
                            href="https://aistudio.google.com/apikey"
                            target="_blank"
                            rel="noreferrer noopener"
                          >
                            aistudio.google.com/apikey
                          </a>
                        </li>
                        <li>با حساب گوگل خود وارد شوید.</li>
                        <li>
                          روی <b>Create API key</b> کلیک کنید و یک کلید جدید
                          بسازید.
                        </li>
                        <li>
                          کلید ساخته‌شده را کپی کنید و در بخش «افزودن API Key»
                          همین صفحه ثبت کنید.
                        </li>
                      </ol>
                      <div className="mt-2">
                        راهنمای رسمی گوگل:{' '}
                        <a
                          href="https://ai.google.dev/gemini-api/docs/api-key"
                          target="_blank"
                          rel="noreferrer noopener"
                        >
                          ai.google.dev/gemini-api/docs/api-key
                        </a>
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
                <style>{`
                  .gemini-guide-accordion {
                    --bs-accordion-btn-color: var(--bs-body-color);
                    --bs-accordion-active-color: var(--bs-body-color);
                    --bs-accordion-btn-bg: rgba(255, 193, 7, 0.12);
                    --bs-accordion-active-bg: rgba(255, 193, 7, 0.2);
                    --bs-accordion-btn-focus-box-shadow: none;
                  }
                  .gemini-guide-accordion .accordion-button {
                    background-color: var(--bs-accordion-btn-bg);
                    color: var(--bs-body-color);
                    box-shadow: none;
                    padding-top: 0.75rem;
                    padding-bottom: 0.75rem;
                  }
                  .gemini-guide-accordion .accordion-button:not(.collapsed) {
                    background-color: var(--bs-accordion-active-bg);
                    color: var(--bs-body-color);
                    box-shadow: none;
                  }
                  .gemini-guide-accordion .accordion-button:focus {
                    box-shadow: none;
                    border-color: var(--bs-warning);
                  }
                  .gemini-guide-accordion .accordion-button::after {
                    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%236e7891'%3e%3cpath fill-rule='evenodd' d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3e%3c/svg%3e");
                  }
                  [data-bs-theme='dark'] .gemini-guide-accordion .accordion-button::after {
                    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%239da9bb'%3e%3cpath fill-rule='evenodd' d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3e%3c/svg%3e");
                  }
                  .gemini-guide-accordion .accordion-item {
                    border-radius: 0.375rem;
                    overflow: hidden;
                    background-color: transparent;
                  }
                `}</style>
              </>
            )}
          </Card.Body>

          {aiMode === 'custom' && (
            <ProviderKeysTable
              isLoading={isFetchingAiUserSettings}
              providerKeys={providerKeysState}
              onToggleApiKeyStatus={handleToggleApiKeyStatus}
              onDeleteApiKey={handleDeleteApiKey}
              isDeletingApiKey={isDeletingProviderKey}
            />
          )}
        </Card>
      </Col>
    </Row>
  );
};

export default AiSettings;
